import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Stack, Typography } from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import { BarChart } from "@mui/x-charts/BarChart";
import { ChartsReferenceLine } from "@mui/x-charts/ChartsReferenceLine";
import {
  CustomChip,
  CustomSectionTitle,
  CustomSummaryCard,
  CustomLoading,
  colors,
} from "@ippon/shared-components";
import { formatSignedYen, formatYen } from "@ippon/shared-utils";
import type { AppDispatch, RootState } from "@/store";
import {
  fetchBudgetAnalysis,
  type BudgetAnalysisProperty,
} from "@/store/budgetAnalysisSlice";
import {
  aggregateComparableNoi,
  aggregateNoiAvailability,
  aggregateTotals,
  computeMonthProgress,
  resolveFinancialValueTone,
} from "./budgetAnalysisAgg";
import { BackButton } from "@/common/BackButton";

const MONTH_LABELS: Record<string, string> = {
  "01": "1月",
  "02": "2月",
  "03": "3月",
  "04": "4月",
  "05": "5月",
  "06": "6月",
  "07": "7月",
  "08": "8月",
  "09": "9月",
  "10": "10月",
  "11": "11月",
  "12": "12月",
};

const CHART_BUDGET_COLOR = "#DBF8FF";
const CHART_ACTUAL_COLOR = "#0B9DBD";
const CHART_TODAY_COLOR = "#F5A623";
const CHART_HIGHLIGHT_COLOR = "#9E9E9E";

const fmtYen = (n: number): string =>
  `${n.toLocaleString("ja-JP", { maximumFractionDigits: 0 })}円`;

const fmtZero = formatYen;

const financialValueColor = (value: number, available = true): string => {
  const tone = resolveFinancialValueTone(value, available);
  if (tone === "negative") return colors.status.error;
  if (tone === "unavailable") return colors.text.secondary;
  return colors.text.primary;
};

const fmtChart = (n: number | null | undefined): string => {
  const v = n ?? 0;
  if (v === 0) return "0";
  if (Math.abs(v) >= 100_000_000) return `${(v / 100_000_000).toFixed(1)}億`;
  if (Math.abs(v) >= 10_000) return `${Math.round(v / 10_000)}万`;
  return v.toLocaleString("ja-JP");
};

function ChartLegend() {
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: 1,
            backgroundColor: CHART_BUDGET_COLOR,
            border: "1px solid #E0E0E0",
          }}
        />
        <Typography sx={{ fontSize: "12px", color: "#666" }}>予算</Typography>
      </Stack>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: 1,
            backgroundColor: CHART_ACTUAL_COLOR,
          }}
        />
        <Typography sx={{ fontSize: "12px", color: "#666" }}>実績</Typography>
      </Stack>
    </Stack>
  );
}

function ChartTooltipContent({ series, dataIndex, axisValue }: any) {
  if (dataIndex == null) return null;
  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        border: "1px solid #E0E0E0",
        borderRadius: "4px",
        p: 1,
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        minWidth: 120,
      }}
    >
      {axisValue != null && (
        <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
          {String(axisValue)}
        </Typography>
      )}
      {series.map(({ id, label, valueFormatter, data, color }: any) => {
        const val = valueFormatter?.(data[dataIndex]) ?? data[dataIndex];
        if (val == null) return null;
        return (
          <Stack
            key={id}
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ py: 0.25 }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: color,
              }}
            />
            <Typography
              sx={{
                fontSize: 12,
                color:
                  Number(data[dataIndex] ?? 0) < 0
                    ? colors.status.error
                    : "#3E3E3E",
              }}
            >
              {label}: {val}
            </Typography>
          </Stack>
        );
      })}
    </Box>
  );
}

/**
 * 予実分析「グループ詳細」全画面ページ。
 * 予実分析のグラフドロワー(PreviewDrawer)の全画面ボタン(onExpand)から遷移する。
 * route: /budget-analysis/group/:groupName?fiscal_year=YYYY
 * パンくず: 予実分析 > {グループ名} (MainLayout の routeConfig で定義)
 */
const BudgetAnalysisGroupPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { groupName: rawGroupName } = useParams<{ groupName: string }>();
  const [searchParams] = useSearchParams();
  const fiscalYear =
    searchParams.get("fiscal_year") || String(new Date().getFullYear());
  const groupName = decodeURIComponent(rawGroupName || "");

  const { data, loading } = useSelector(
    (s: RootState) => (s as any).budgetAnalysis,
  );
  const fiscalClosingMonth: number =
    useSelector(
      (s: RootState) =>
        (s.clients?.detailed?.data as any)?.fiscal_closing_month,
    ) ?? 3;
  const fiscalYearStartMonth: number =
    fiscalClosingMonth === 12 ? 1 : fiscalClosingMonth + 1;
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    dispatch(fetchBudgetAnalysis({ fiscal_year: fiscalYear }));
  }, [dispatch, fiscalYear]);

  const monthKeys: string[] = useMemo(() => data?.month_keys ?? [], [data]);
  // 「前月まで」は撤廃済み: 全月(=データのある月)を対象にする
  const completedCount = monthKeys.length;

  const groupProperties: BudgetAnalysisProperty[] = useMemo(() => {
    const props = (data?.properties ?? []) as BudgetAnalysisProperty[];
    return props.filter((p) => (p.group_name || "グループなし") === groupName);
  }, [data, groupName]);

  const totals = useMemo(
    () => aggregateTotals(groupProperties, completedCount),
    [groupProperties, completedCount],
  );
  const comparableNoi = useMemo(
    () => aggregateComparableNoi(groupProperties, completedCount),
    [groupProperties, completedCount],
  );
  const noiDiff = comparableNoi.diff;
  const availability = useMemo(
    () => aggregateNoiAvailability(groupProperties, completedCount),
    [groupProperties, completedCount],
  );
  const monthLabels = useMemo(
    () => monthKeys.map((mk) => MONTH_LABELS[mk] || mk),
    [monthKeys],
  );
  const { currentYear, currentMonth } = useMemo(() => {
    const now = new Date();
    return { currentYear: now.getFullYear(), currentMonth: now.getMonth() + 1 };
  }, []);
  const { currentMonthIndex } = useMemo(
    () =>
      computeMonthProgress(
        monthKeys,
        fiscalYearStartMonth,
        Number(fiscalYear),
        currentYear,
        currentMonth,
      ),
    [monthKeys, fiscalYearStartMonth, fiscalYear, currentYear, currentMonth],
  );
  const selectedProperty = useMemo(() => {
    if (!groupProperties.length) return null;
    return (
      groupProperties.find((p) => p.property_id === selectedPropertyId) ||
      groupProperties[0]
    );
  }, [groupProperties, selectedPropertyId]);
  useEffect(() => {
    if (!groupProperties.length) {
      setSelectedPropertyId(null);
      return;
    }
    if (!selectedPropertyId) {
      setSelectedPropertyId(groupProperties[0].property_id);
      return;
    }
    if (!groupProperties.some((p) => p.property_id === selectedPropertyId)) {
      setSelectedPropertyId(groupProperties[0].property_id);
    }
  }, [groupProperties, selectedPropertyId]);
  const propertyBudgetNoi =
    selectedProperty?.monthly.map(
      (m) => (m.income_budget || 0) - (m.expense_budget || 0),
    ) ?? [];
  const propertyActualNoi =
    selectedProperty?.monthly.map(
      (m) => (m.income_actual || 0) - (m.expense_actual || 0),
    ) ?? [];
  const selectedTotals = selectedProperty
    ? aggregateTotals([selectedProperty], completedCount)
    : { noiActual: 0, noiBudget: 0 };
  const propertyBarData = groupProperties.map((p) => {
    const t = aggregateTotals([p], completedCount);
    return {
      name: p.name.length > 12 ? `${p.name.slice(0, 12)}…` : p.name,
      budget: t.noiBudget,
      actual: t.noiActual,
    };
  });

  if (loading && !data) {
    return <CustomLoading loading />;
  }

  return (
    <Box sx={{ pb: 9, pt: 0, px: 2 }}>
      <Box
        sx={{
          mb: 3,
          pt: 0.5,
          position: "sticky",
          top: 50,
          zIndex: 10,
          backgroundColor: "white",
          mx: -2,
          px: 2,
        }}
      >
        <CustomSectionTitle
          title={groupName || "グループ詳細"}
          size="large"
          addBorder
          icon={<BackButton size={24} />}
          noTruncate
        />
      </Box>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <CustomSummaryCard
          label="NOI実績合計"
          value={
            availability.noiActualAvailable ? fmtYen(totals.noiActual) : "-"
          }
          valueColor={financialValueColor(
            totals.noiActual,
            availability.noiActualAvailable,
          )}
          loading={loading}
          loadingDelayMs={150}
          sx={{ flex: 1 }}
        />
        <CustomSummaryCard
          label="NOI予算合計"
          value={
            availability.noiBudgetAvailable ? fmtYen(totals.noiBudget) : "-"
          }
          valueColor={financialValueColor(
            totals.noiBudget,
            availability.noiBudgetAvailable,
          )}
          loading={loading}
          loadingDelayMs={150}
          sx={{ flex: 1 }}
        />
        <CustomSummaryCard
          label="差異"
          value={
            comparableNoi.available
              ? `${noiDiff >= 0 ? "+" : ""}${fmtYen(noiDiff)}`
              : "-"
          }
          valueColor={
            !comparableNoi.available
              ? "#666"
              : noiDiff >= 0
                ? "#2E7D32"
                : colors.status.error
          }
          loading={loading}
          loadingDelayMs={150}
          sx={{
            flex: 1,
            ...(comparableNoi.available &&
              noiDiff < 0 && { backgroundColor: "#FFF0F0" }),
          }}
        />
      </Stack>

      {groupProperties.length > 0 ? (
        <>
          <Box
            sx={{
              p: 2,
              backgroundColor: "#fff",
              borderRadius: "8px",
              border: "1px solid #E0E0E0",
              mb: 2,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                sx={{ fontSize: "14px", fontWeight: 600, color: "#3E3E3E" }}
              >
                NOI 予実推移
              </Typography>
              <ChartLegend />
            </Stack>
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ mb: 1, mt: 1, flexWrap: "wrap" }}
            >
              {groupProperties.map((p) => (
                <Button
                  key={p.property_id}
                  size="small"
                  variant={
                    selectedProperty?.property_id === p.property_id
                      ? "contained"
                      : "text"
                  }
                  onClick={() => setSelectedPropertyId(p.property_id)}
                  disableElevation
                  sx={{
                    fontSize: "12px",
                    textTransform: "none",
                    borderRadius: "20px",
                    boxShadow: "none",
                    ...(selectedProperty?.property_id === p.property_id
                      ? {
                          backgroundColor: colors.primary.main,
                          color: "#fff",
                          "&:hover": {
                            backgroundColor: colors.primary.main,
                            boxShadow: "none",
                          },
                        }
                      : { color: "#666", "&:hover": { boxShadow: "none" } }),
                  }}
                >
                  {p.name}
                </Button>
              ))}
            </Stack>
            {selectedProperty && (
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <CustomChip
                  label={`実績 ${fmtZero(selectedTotals.noiActual)}`}
                  fontSize="12px"
                  backgroundColor="#F5F5F5"
                  color={financialValueColor(selectedTotals.noiActual)}
                  sx={{ borderRadius: "10px" }}
                />
                <CustomChip
                  label={`予算 ${fmtZero(selectedTotals.noiBudget)}`}
                  fontSize="12px"
                  backgroundColor="#F5F5F5"
                  color={financialValueColor(selectedTotals.noiBudget)}
                  sx={{ borderRadius: "10px" }}
                />
                <CustomChip
                  label={`差異 ${formatSignedYen(selectedTotals.noiActual - selectedTotals.noiBudget)}`}
                  fontSize="12px"
                  sx={{ borderRadius: "10px" }}
                  backgroundColor={
                    selectedTotals.noiActual - selectedTotals.noiBudget >= 0
                      ? "#E8F5E9"
                      : "#FFEBEE"
                  }
                  color={
                    selectedTotals.noiActual - selectedTotals.noiBudget >= 0
                      ? "#2E7D32"
                      : colors.status.error
                  }
                />
              </Stack>
            )}
            <Box sx={{ height: 320 }}>
              {monthLabels.length > 0 &&
                monthLabels.length === propertyBudgetNoi.length &&
                monthLabels.length === propertyActualNoi.length && (
                  <LineChart
                    xAxis={[{ data: monthLabels, scaleType: "point" }]}
                    yAxis={[
                      {
                        valueFormatter: (v: number | null) => fmtChart(v),
                      },
                    ]}
                    series={[
                      {
                        id: "group-page-budget",
                        data: propertyBudgetNoi,
                        label: "予算",
                        color: CHART_BUDGET_COLOR,
                        showMark: false,
                        valueFormatter: (v: number | null) => fmtZero(v ?? 0),
                      },
                      {
                        id: "group-page-actual",
                        data: propertyActualNoi,
                        label: "実績",
                        color: CHART_ACTUAL_COLOR,
                        showMark: true,
                        valueFormatter: (v: number | null) => fmtZero(v ?? 0),
                      },
                    ]}
                    height={320}
                    slots={{ axisContent: ChartTooltipContent }}
                    slotProps={{ legend: { hidden: true } }}
                    sx={{
                      "& .MuiLineElement-series-group-page-budget": {
                        strokeDasharray: "6 4",
                      },
                      "& .MuiChartsAxisHighlight-root": {
                        stroke: CHART_HIGHLIGHT_COLOR,
                      },
                    }}
                  >
                    {currentMonthIndex >= 0 && (
                      <ChartsReferenceLine
                        x={monthLabels[currentMonthIndex]}
                        label="今月"
                        labelAlign="start"
                        lineStyle={{
                          stroke: CHART_TODAY_COLOR,
                          strokeDasharray: "4 3",
                          strokeWidth: 1.5,
                        }}
                        labelStyle={{
                          fontSize: 11,
                          fill: CHART_TODAY_COLOR,
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </LineChart>
                )}
            </Box>
          </Box>

          <Box
            sx={{
              p: 2,
              backgroundColor: "#fff",
              borderRadius: "8px",
              border: "1px solid #E0E0E0",
              mb: 2,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Box>
                <Typography
                  sx={{ fontSize: "14px", fontWeight: 600, color: "#3E3E3E" }}
                >
                  NOI 累計比較
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#666" }}>
                  物件別の{fiscalYear}年度累計 予算 vs 実績
                </Typography>
              </Box>
              <ChartLegend />
            </Stack>
            <Box sx={{ height: Math.max(propertyBarData.length * 50, 150) }}>
              <BarChart
                yAxis={[
                  {
                    data: propertyBarData.map((d) => d.name),
                    scaleType: "band",
                  },
                ]}
                xAxis={[
                  {
                    valueFormatter: (v: number | null) => fmtChart(v),
                  },
                ]}
                series={[
                  {
                    data: propertyBarData.map((d) => d.budget),
                    label: "予算",
                    color: CHART_BUDGET_COLOR,
                  },
                  {
                    data: propertyBarData.map((d) => d.actual),
                    label: "実績",
                    color: CHART_ACTUAL_COLOR,
                  },
                ]}
                layout="horizontal"
                height={Math.max(propertyBarData.length * 50, 150)}
                margin={{ left: 170, right: 20, top: 10, bottom: 30 }}
                slots={{ axisContent: ChartTooltipContent }}
                slotProps={{ legend: { hidden: true } }}
              />
            </Box>
          </Box>
        </>
      ) : (
        <Typography sx={{ color: colors.text.secondary, mt: 2 }}>
          このグループの物件が見つかりませんでした。
        </Typography>
      )}
    </Box>
  );
};

export default BudgetAnalysisGroupPage;
